import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, FileText, Mail, AlertTriangle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const CONTACT_EMAIL = "contact@misyou.in";

export default function TermsConditionsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms & Conditions</h1>
          <p className="text-muted-foreground">Effective Date: 17th February 2026</p>
        </div>

        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">
            
            <p className="lead text-lg text-slate-600">
              By using the MISYOU application ("App"), you agree to be bound by these Terms & Conditions. Please read them carefully before using our services.
            </p>

            <h2>1. Purpose of the App</h2>
            <p>
              MISYOU is a platform designed to help report and locate missing persons through community participation. 
              <strong> It is NOT a law enforcement service, government agency, or official investigation body.</strong> 
              This app is a supplementary tool and should not replace official police reporting.
            </p>

            <h2>2. Age Requirement</h2>
            <p>
              You must be at least <strong>18 years old</strong> to create an account and use this app. 
              Users under 18 may use the app only under the supervision and consent of a parent or legal guardian.
            </p>

            <h2>3. User Responsibilities</h2>
            <p>By using this app, you agree to:</p>
            <ul>
              <li>Provide accurate, truthful, and complete information</li>
              <li>Not post false, misleading, defamatory, or harmful content</li>
              <li>Respect the privacy and legal rights of others</li>
              <li>Not use the app for any illegal or unauthorized purpose</li>
              <li>Keep your account credentials secure</li>
              <li>Report any suspicious or false listings you encounter</li>
              <li>
                <strong>Not publish personal contact information</strong> (phone numbers, email addresses, social
                media handles, home address) in case descriptions, sightings, comments, or any other public-facing
                field. MISYOU provides a secure in-app contact mechanism for this purpose.
              </li>
              <li>
                <strong>Not contact other users to demand, extort, or solicit money</strong> in exchange for information,
                sightings, "finder's fees", or any other consideration related to a missing person.
              </li>
            </ul>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6" data-testid="terms-extortion-clause">
              <h3 className="flex items-center gap-2 text-red-800 mt-0">
                <AlertTriangle className="w-5 h-5" />
                Anti-Extortion Policy &mdash; ZERO TOLERANCE
              </h3>
              <p className="text-red-700">
                Any attempt to extort, defraud, harass or coerce another MISYOU user (including but not limited to
                demanding money in exchange for information about a missing person) is:
              </p>
              <ul className="text-red-700">
                <li>A serious criminal offence under Indian law (IPC Sections 384&ndash;389, 503, and 507).</li>
                <li>Grounds for <strong>immediate and permanent ban</strong> of the offending account.</li>
                <li>Reported by MISYOU to local law-enforcement authorities, with all available evidence (account details, IP, suspect phone &amp; email, call timestamps).</li>
              </ul>
              <p className="text-red-700 font-semibold mb-0">
                MISYOU advises every user: don&apos;t give any money to anyone without involving your local police station &mdash; always keep your local police informed.
              </p>
              <p className="text-red-700 mt-3 mb-0 text-sm">
                See our full <Link to="/anti-harassment" className="underline font-semibold">Anti-Harassment &amp; Misuse Policy</Link>
                {' '}for the complete list of prohibited behaviours, the evidence we retain (IP, user-agent, timestamps, suspect phone &amp; email, scam-keyword hits, content hashes), and the cybercrime channels we cooperate with (
                <a href="https://cybercrime.gov.in/" target="_blank" rel="noreferrer" className="underline">cybercrime.gov.in</a>, helpline 1930).
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6">
              <h3 className="flex items-center gap-2 text-red-800 mt-0">
                <AlertTriangle className="w-5 h-5" />
                4. False Reports - STRICT POLICY
              </h3>
              <p className="text-red-700 mb-0">
                Submitting false or misleading missing person reports is:
              </p>
              <ul className="text-red-700">
                <li>A serious violation of these terms</li>
                <li>Potentially a criminal offense under Indian law (IPC Section 182, 211)</li>
                <li>Grounds for immediate and permanent account termination</li>
                <li>Subject to legal action and reporting to law enforcement</li>
              </ul>
              <p className="text-red-700 mb-0">
                <strong>We actively cooperate with police authorities to prosecute false reports.</strong>
              </p>
            </div>

            <h2>5. Photo Upload Consent</h2>
            <p>By uploading photos to this app, you confirm and warrant that:</p>
            <ul>
              <li>You own the rights to the image OR have explicit permission to share it</li>
              <li>The photo is genuinely of the missing person you are reporting</li>
              <li>You accept full legal responsibility for any issues arising from the photo</li>
              <li>You consent to the photo being displayed publicly</li>
            </ul>

            <h2>6. No Guarantee of Results</h2>
            <p>
              We make <strong>NO guarantees</strong> that any missing person will be found through this platform. 
              Success depends on various factors including community participation, accuracy of information provided, 
              and circumstances beyond our control.
            </p>

            <h2>7. Content Moderation</h2>
            <p>
              We reserve the right to review, edit, or remove any content at our sole discretion and without prior notice. 
              This includes but is not limited to content that:
            </p>
            <ul>
              <li>Appears to be false or misleading</li>
              <li>Violates these terms or applicable laws</li>
              <li>Is reported by other users</li>
              <li>Contains inappropriate or harmful material</li>
            </ul>

            <h2>8. Subscription & Payment Terms</h2>
            <ul>
              <li><strong>Free Trial:</strong> New users receive a 15-day free trial period</li>
              <li><strong>Subscription:</strong> After the trial, a paid subscription is required for full features</li>
              <li><strong>Payment:</strong> Payments are processed through secure third-party payment gateways</li>
              <li><strong>Non-Refundable:</strong> Payments are generally non-refundable unless required by law</li>
              <li><strong>Price Changes:</strong> We reserve the right to change pricing with reasonable notice</li>
            </ul>

            <h2>9. Refund Policy</h2>
            <p>Refunds may be considered only in the following cases:</p>
            <ul>
              <li>Technical errors that prevented service delivery</li>
              <li>Duplicate or erroneous payments</li>
              <li>Service unavailability for extended periods due to our fault</li>
            </ul>
            <p>Refund requests must be submitted within <strong>7 days</strong> of payment to {CONTACT_EMAIL}.</p>

            <h2>10. Limitation of Liability</h2>
            <p>MISYOU and its owners, employees, and partners are NOT responsible for:</p>
            <ul>
              <li>Incorrect, misleading, or false information posted by users</li>
              <li>Any direct, indirect, incidental, or consequential damages arising from app use</li>
              <li>Actions taken based on information found on this app</li>
              <li>Loss of data due to technical failures</li>
              <li>Third-party actions or content</li>
            </ul>
            <p>
              <strong>Maximum liability is limited to the amount paid by the user to MISYOU (if any) in the 12 months preceding the claim.</strong>
            </p>

            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless MISYOU, its owners, employees, agents, and partners from any claims, 
              damages, losses, liabilities, costs, or expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use or misuse of the app</li>
              <li>Information or content you submit</li>
              <li>Your violation of these terms</li>
              <li>Your violation of any third-party rights</li>
            </ul>

            <h2>12. Account Suspension & Termination</h2>
            <p>We may suspend or permanently terminate your account without notice if you:</p>
            <ul>
              <li>Violate these Terms & Conditions</li>
              <li>Submit false or misleading reports</li>
              <li>Engage in fraudulent or illegal activity</li>
              <li>Harass other users or our staff</li>
              <li>Attempt to manipulate or abuse the platform</li>
            </ul>

            <h2>13. Intellectual Property</h2>
            <p>
              All app content, design, logos, and features (except user-submitted content) are the intellectual property of MISYOU 
              and are protected by copyright and trademark laws. You may not copy, reproduce, or distribute our content without permission.
            </p>

            <h2>14. Dispute Resolution</h2>
            <ul>
              <li><strong>Mediation First:</strong> Any disputes shall first be attempted to be resolved through good-faith mediation</li>
              <li><strong>Arbitration:</strong> If mediation fails, disputes will be resolved through binding arbitration in Bathinda, Punjab, India</li>
              <li><strong>Jurisdiction:</strong> Courts in Bathinda, Punjab, India shall have exclusive jurisdiction</li>
              <li><strong>Class Action Waiver:</strong> You agree to resolve disputes individually and waive any right to class action proceedings</li>
            </ul>

            <h2>15. Force Majeure</h2>
            <p>
              We are not liable for any failure or delay in performing our obligations due to circumstances beyond our reasonable control, 
              including but not limited to natural disasters, cyber attacks, government actions, pandemics, or infrastructure failures.
            </p>

            <h2>16. Emergency Situations & Contact Information</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
              <p className="font-semibold text-amber-800 mb-3">This app is NOT for emergencies.</p>
              
              <p className="font-semibold text-amber-800 mb-2">Government Emergency Helplines (India):</p>
              <p className="text-amber-700 text-sm mb-2">For immediate emergency assistance, contact these official government agencies:</p>
              <ul className="text-amber-700 mb-4">
                <li><strong>Police Emergency:</strong> 100</li>
                <li><strong>Women Helpline:</strong> 181</li>
                <li><strong>Child Helpline (CHILDLINE):</strong> 1098</li>
                <li><strong>Missing Persons:</strong> File FIR at your local police station</li>
              </ul>
              
              <p className="font-semibold text-amber-800 mb-2">MISYOU Customer Support:</p>
              <p className="text-amber-700 text-sm mb-2">For app-related queries, technical support, or general assistance:</p>
              <ul className="text-amber-700 mb-0">
                <li><strong>Phone:</strong> +91 81464 99739</li>
                <li><strong>Email:</strong> <a href="mailto:contact@misyou.in" className="underline">contact@misyou.in</a></li>
              </ul>
            </div>

            <h2>17. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms & Conditions at any time. Significant changes will be communicated through the app 
              or via email. Your continued use of the app after changes constitutes acceptance of the modified terms.
            </p>

            <h2>18. Governing Law</h2>
            <p>
              These Terms & Conditions are governed by and construed in accordance with the laws of India. 
              Any legal proceedings shall be conducted in English.
            </p>

            <h2>19. Severability</h2>
            <p>
              If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>

            <h2>20. Contact</h2>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-primary" />
                <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
              <p className="mb-2"><strong>Phone:</strong> +91 81464 99739</p>
              <p className="mb-2"><strong>Business Name:</strong> MISYOU</p>
              <p><strong>Jurisdiction:</strong> Bathinda, Punjab, India</p>
            </div>

          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/privacy">
            <Button variant="outline">Privacy Policy</Button>
          </Link>
          <Link to="/disclaimer">
            <Button variant="outline">Disclaimer</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
